import React, { MouseEvent, useCallback, useState } from 'react';
import {
  IconButton,
  icons,
  Menu,
  MenuItem,
  theme,
  Tooltip,
} from '@ublend-npm/aulaui-next';
import { useSlateStatic } from 'slate-react';
import { HeadingFormats } from '../../../plugins/headings/headings';
import { getCurrentNodeType } from '../../../lib/queries';
import { hotkeys } from '../../../config/hotkeys';
import { HotkeyLabels } from '../../HotkeyLabel/HotkeyLabel';
import { toggleBlockType } from '../../../lib/commands';
import { TooltipTitle } from '../../HotkeyLabel/HotkeyLabel.styled';
import { blockQuoteType } from '../../../plugins/blockQuote/blockQuoteType';
import { AulaRange } from '../../../types';

const { H1, H2, H3, TitleIcon, BlockQuote } = icons;
const headerIcons = {
  h1: H1,
  h2: H2,
  h3: H3,
  blockquote: BlockQuote,
};

const tooltip = (
  <TooltipTitle>
    Text styles
    <HotkeyLabels hotkeys={hotkeys.heading} />
  </TooltipTitle>
);

export const HeaderButton = React.forwardRef(
  (
    {
      lastActiveSelection,
      menuRef,
    }: {
      lastActiveSelection: AulaRange;
      menuRef: React.MutableRefObject<unknown>;
    },
    ref,
  ) => {
    const editor = useSlateStatic();
    const [showTooltip, setShowTooltip] = useState(false);
    const currentType = getCurrentNodeType(editor);

    const icon = headerIcons[currentType as HeadingFormats] || TitleIcon;

    const onToggleBlockType = useCallback(
      (blockType?: string) =>
        (event: MouseEvent<HTMLButtonElement> | React.KeyboardEvent) => {
          event.preventDefault();
          event.stopPropagation();
          toggleBlockType(editor, blockType, lastActiveSelection);
        },
      [editor, lastActiveSelection],
    );

    const items = [
      { label: 'Large heading', Icon: H1, format: HeadingFormats.h1 },
      { label: 'Medium heading', Icon: H2, format: HeadingFormats.h2 },
      { label: 'Small heading', Icon: H3, format: HeadingFormats.h3 },
      { label: 'Quote', Icon: BlockQuote, format: blockQuoteType },
      { label: 'Paragraph text', Icon: TitleIcon, format: undefined },
    ];

    const renderMenuItems = ({ getItemProps }: any) =>
      items.map(({ label, Icon, format }) => (
        <MenuItem
          key={label}
          size="small"
          leftDecoration={<Icon style={{ color: theme.color.grey11 }} />}
          primaryText={label}
          {...getItemProps({
            item: () => {},
            onClick: onToggleBlockType(format),
          })}
          attributes={{
            'aria-label': label,
          }}
        />
      ));

    const renderButton = ({ handleTargetClick, isOpen, hide }: any) => (
      <Tooltip title={tooltip} open={showTooltip}>
        <div
          // Fix for Safari issue where the heading menu closes immediately on click
          onPointerDown={(event: React.SyntheticEvent) =>
            event.preventDefault()
          }
        >
          <IconButton
            label="Select heading"
            icon={icon}
            type="secondary"
            size="small"
            caret={isOpen ? 'up' : 'down'}
            onClick={(event: React.SyntheticEvent) => {
              setShowTooltip(false);

              if (isOpen) {
                hide();
              } else {
                handleTargetClick(event);
              }
            }}
            buttonAttributes={{
              onMouseEnter: () => setShowTooltip(true),
              onMouseLeave: () => setShowTooltip(false),
            }}
            ref={ref}
          />
        </div>
      </Tooltip>
    );
    const menuAttributes = {
      role: 'listbox',
      'aria-label': 'Section heading type',
    };

    return (
      <Menu
        ref={menuRef}
        elevated
        spacing={theme.spacing.xxSmall}
        menuListComponent="ul"
        menuListAttributes={menuAttributes}
        ariaLabelledby="heading-type"
        renderTarget={renderButton}
        renderChildren={renderMenuItems}
        verticalSwitchThreshold={250}
        css={{ zIndex: 1500 }}
        disableAutoFocus
      />
    );
  },
);

HeaderButton.displayName = 'HeaderButton';
